(ns ductile.ui.report-damages
  (:require ["framer-motion" :as framer-motion]
            ["signature_pad" :as signature-pad]
            [clojure.string :as str]
            [ductile.ui.components :as ui]
            [ductile.ui.deframe :as deframe]
            [ductile.ui.http :as http]
            [ductile.ui.locale :as locale :refer [tr]]
            [ductile.ui.mobile :as mobile]
            [ductile.ui.workflow :as workflow]
            [kitchen-async.promise :as p]
            [nextjournal.clerk.render.hooks :as hooks]
            [reagent.core :as r]))

;; ✓ Add damages via graphic to deframe `::damages` (use `deframe/route-assoc...`)
;;   ✓ show little number badge on graphic
;; ✓ Show matching litte number badge in damage items
;; * Show remove damage link
;; * Add photo/photo(s) to each damage
;; * Remove damage confirmation modal
;; * Submit confirmation modal
;; * Show vehicle information
;; * simple selector to select damage region (80 sections)
;; * create region polygon editor
;; * find out region of the car (e.g. left front door)
;;   and show it with the damage item

(defn modify-damage! [path idx damage]
  (deframe/route-assoc-in! (conj path idx) (merge @(deframe/!route-get-in (conj path idx))
                                                  damage)))

(defn add-new-damage! [path damage]
  (deframe/route-assoc-in! path
                           (if (vector? @(deframe/!route-get-in path))
                             (conj @(deframe/!route-get-in path [])
                                   damage)
                             [damage])))

(defn remove-at [coll i]
  (vec (concat (subvec coll 0 i)
               (subvec coll (inc i)))))

(defn remove-damage! [path idx]
  (deframe/route-assoc-in! path (remove-at @(deframe/!route-get-in path [])
                                           idx)))

;; `transport-ops-vehicle` and `signature` are copied and modified from `transport.cljc`
(defn transport-ops-vehicle-header [fahrzeug]
  (let [is-new? @(deframe/!route-get ::is-new? true)
        is-protokoll-available? @(deframe/!route-get ::is-protokoll-available? false)]
    [:div.bg-white.rounded-lg.mx-3.mt-3
     [:div.px-3.py-2.border-b
      [:div.flex.text-sm.gap-2
       [:span.font-bold (:vehicle/model fahrzeug)]
       [:span (:vehicle/vin fahrzeug)]]
      [:div.text-xs.mt-1 (str/join ", " (:vehicle/equipment fahrzeug))]
      [:div.flex.items-center.mt-1
       {:class (if (:vehicle/weight fahrzeug) "justify-between" "justify-start")}
       (when (:vehicle/weight fahrzeug)
         [:span.text-xs (:vehicle/weight fahrzeug) "kg"])]]

     [:div.flex.items-center.gap-4.py-2.px-3
      [:div.flex.items-center.gap-1
       [:input {:id "a"
                :type :radio
                :checked is-new?
                :on-change #(deframe/route-assoc! ::is-new? true)}]
       [:label.text-sm {:for "a"} "Neufahrzeug"]]
      [:div.flex.items-center.gap-1
       [:input {:id "b"
                :type :radio
                :checked (not is-new?)
                :on-change #(deframe/route-assoc! ::is-new? false)}]
       [:label.text-sm {:for "b"} "Gebrauchfahrzeug"]]]

     (when (not is-new?) [:div.flex.items-center.gap-4.py-2.pl-6.pr-3.text-sm
                          "Übernahmeprotokoll/ Zustandsbericht vorhanden?"
                          [:div.flex.items-center.gap-1
                           [:input {:id "d"
                                    :type :radio
                                    :checked is-protokoll-available?
                                    :on-change #(deframe/route-assoc! ::is-protokoll-available? true)}]
                           [:label.font-bold {:for "d"} "JA"]]
                          [:div.flex.items-center.gap-1
                           [:input {:id "c"
                                    :type :radio
                                    :checked (not is-protokoll-available?)
                                    :on-change #(deframe/route-assoc! ::is-protokoll-available? false)}]
                           [:label.font-bold {:for "c"} "NEIN"]]])]))
(defn signature [{:keys [label]}]
  (let [!canvas-container (hooks/use-ref nil)]
    (hooks/use-effect
     (fn []
       (when-let [el @!canvas-container]
         (let [rect (.getBoundingClientRect el)
               canvas (.querySelector el "canvas")]
           (set! (.. canvas -width) (str (.-width rect)))
           (signature-pad. canvas))))
     [@!canvas-container])
    [:div.bg-white.rounded-lg.mx-3.mt-3.p-3
     [:div.w-full.rounded.border.border-slate-300.shadow-inner.relative
      {:ref !canvas-container}
      [:canvas {:height 100}]
      [:div.absolute.border-t.border-slate-300.border-dashed.left-0.right-0.pointer-events-none {:class "bottom-[24px]"}]
      [:div.text-xs.py-1.px-3 label]]]))

(defn damage-photos [{:keys [entity notice !state]}]
  (let [{:keys [photos service]} @!state]
    [:<>
     [:div.mr-0.pr-4.pt-2
      [mobile/add-image-button {:on-click #(swap! !state assoc :taking-photo? true)
                                :label (tr "Add photo")}]]
     (when (seq photos)
       [:div.py-1
        (into [:div.flex.flex-wrap.gap-2]
              (map (fn [{:ductile/keys [id] :keys [url] :cas/keys [multihash]}]
                     [mobile/image-tile
                      {:url url
                       :remove-fn #(p/let [result (http/$lambda
                                                   'ductile.damages/handle-remove-photo
                                                   {:entity-id (:ductile/id entity)
                                                    :cas-id id})]
                                     (if (:error result)
                                       (reset! notice result)
                                       (swap! !state
                                              update :photos
                                              (partial remove (fn [cas] (= multihash (:cas/multihash cas)))))))}]))
              photos)])]))

(defn damage-badge* [{num :num pos :pos}]
  [:g
   {:transform
    (when pos (str "translate(" (- (first pos) 7) ", " (- (second pos) 6) ")"))}
   [:path
    {:d
     "M4.79907 0.819156L3.44095 3.35368L1.03239 4.38377L2.40816 5.74406L2.04173 8.26647L4.03696 7.98296L5.4899 10.0479L6.72945 8.3781L10.6407 8.57759L9.96379 6.99482L11.6071 4.17551L10.0547 3.78124L8.57228 1.64752L6.98952 2.32444L4.79907 0.819156Z"
     :fill "#eb6c23"
     :stroke "white"
     :stroke-width "0.5"}]
   [:text {:fill "white"
           :font-size "5.23809"
           :font-weight "bold"
           :text-anchor "middle"}
    [:tspan {:x "6.3" :y "7.2"} (inc num)]]])

(defn damage-badge [{num :num}]
  [:svg
   {:xmlns "http://www.w3.org/2000/svg"
    :width "46px"
    :viewBox "0 0 12 12"
    :fill "none"}
   [damage-badge* {:num num}]])

(defn eventToSvgCoordinates [e]
  (let [svg (.-currentTarget e)
        p (.createSVGPoint svg)]
    (set! (.-x p) (.-clientX e))
    (set! (.-y p) (.-clientY e))
    (let [t-p (.matrixTransform p (.inverse (.getScreenCTM svg)))]
      [(js/Number (.toFixed (.-x t-p) 2)) (js/Number (.toFixed (.-y t-p) 2))])))

(defn average [coll]
  (/ (reduce + coll) (count coll)))
(defn polygon-centroid [ps]
  [(average (map first ps))
   (average (map second ps))])

(defn polygon [{key :key ps :ps}]
  (let [c (hooks/use-ref [(* 255 (js/Math.random)) (* 255 (js/Math.random)) (* 255 (js/Math.random))])
        l-col (str "rgba(" (nth @c 0) ", " (nth @c 1) ", " (nth @c 2) ", 0.2)")
        d-col (str "rgba(" (nth @c 0) ", " (nth @c 1) ", " (nth @c 2) ", 1)")]
    [:polygon {:data-key key
               :points (str/join " " (map (fn [[x y]]
                                            (str x "," y))
                                          ps))
               :fill l-col
               :stroke d-col
               :stroke-width 0.5}]))

(defn invisible-polygon [{key :key ps :ps}]
  [:polygon.transition-all {:data-key key
                            :points (str/join " " (map (fn [[x y]]
                                                         (str x "," y))
                                                       ps))
                            :opacity 0
                            :fill "rgb(79, 70, 229)"}])

(def polygons {"Felge hinten links" [[1 68] [8 68] [16 72] [18 78] [14 85] [7 87] [0 87]],
               "Frontgrill" [[30 -17] [26 -3] [75 -1] [72 -17]],
               "Türe vorne links" [[2 28] [1 52] [25 52] [24 26]],
               "Seitenscheibe vorne rechts" [[78 28] [76 52] [65 53] [66 40] [74 29]],
               "Dach" [[33 39] [34 75] [51 78] [66 75] [66 40]],
               "Seitenscheibe hinten rechts" [[65 53] [76 51] [76 84] [66 74]],
               "Seitenteil hinten links" [[16 74] [25 74] [25 83] [30 84] [30 99] [21 104] [1 104] [1 86] [8 86] [14 84] [17 78]],
               "Felge vorne rechts" [[99 7] [92 6] [85 8] [82 13] [84 20] [90 23] [99 23]],
               "Seitenteil vorne links"
               [[3 -4] [4 7] [15 8] [17 17] [11 23] [6 23] [1 24] [2 28] [23 27] [29 30] [25 18] [21 -6] [11 -8]],
               "Seitenteil hinten rechts"
               [[75 73] [83 75] [84 81] [89 86] [99 85] [100 102] [89 105] [78 104] [71 99] [70 84] [76 83]],
               "Felge vorne links" [[3 5] [8 5] [13 6] [17 11] [18 17] [11 24] [1 24] [1 7]],
               "Heckblech" [[22 103] [31 98] [71 98] [80 103] [78 118] [23 117]],
               "Türe hinten links" [[1 51] [25 51] [25 75] [16 74] [13 71] [6 69] [1 68]],
               "Seitenteil vorne rechts"
               [[96 8] [95 -4] [88 -7] [79 -3] [75 20] [75 26] [73 32] [78 27] [99 28] [99 23] [88 22] [83 15] [86 9] [93 7]],
               "Motorhaube" [[21 -2] [27 -3] [74 -2] [79 1] [74 21] [69 24] [59 20] [38 20] [31 23] [25 21]],
               "Frontscheibe" [[28 21] [23 20] [33 40] [67 40] [76 24] [74 20] [69 23] [58 19] [39 18] [35 21]],
               "Heckscheibe" [[33 74] [51 77] [66 74] [76 83] [51 87] [24 83]],
               "Türe hinten rechts" [[75 52] [99 51] [100 70] [91 69] [88 69] [83 75] [75 74]],
               "Heckdeckel" [[30 83] [30 99] [71 99] [71 84] [51 87]],
               "Felge hinten rechts" [[100 69] [94 68] [87 69] [82 75] [84 82] [88 86] [99 86]],
               "Türe vorne rechts" [[77 26] [99 27] [99 52] [75 52]],
               "Seitenscheibe vorne links" [[23 27] [24 52] [35 53] [34 40] [28 28]],
               "Seitenscheibe hinten links" [[24 51] [35 53] [34 75] [24 83]]})

#_(into {} (mapv (fn [[k v]] [(str/capitalize (str/join " " (rest (str/split (name k) #"-")))) v]) polygons))

(def regions [:01-spoiler :02-stoßstange-vorne :03-frontgrill :04-scheinwerfer-links :05-scheinwerfer-rechts :06-motorhaube :07-schiebedach :08-seitenteil-vorne-links :09-seitenteil-vorne-rechts :10-türe-vorne-links :11-türe-vorne-rechts :12-türe-hinten-links :13-türe-hinten-rechts :14-schweller-links :15-schweller-rechts :16-seitenteil-hinten-links :17-seitenteil-hinten-rechts :18-tankklappe :19-dach :20-heckdeckel :21-heckblech :22-stoßstange-hinten :23-heckschürze :24-heckleuchte-links :25-heckleuchte-rechts :26-scheibenwischer-links :27-scheibenwischer-rechts :28-ausstellfenster-links-vorne :29-ausstellfenster-rechts-vorne :30-frontscheibe :31-seitenscheibe-vorne-links :32-seitenscheibe-vorne-rechts :33-seitenscheibe-hinten-links :34-seitenscheibe-hinten-rechts :35-dreiecksfenster-hinten-links :36-dreiecksfenster-hinten-rechts :37-heckscheibe :38-reifen-vorne-links :39-felge-vorne-links :40-reifen-vorne-rechts :41-felge-vorne-rechts :42-reifen-hinten-links :43-felge-hinten-links :44-reifen-hinten-rechts :45-felge-hinten-rechts :46-außenspiegel-links :47-batterie :48-innenspiegel :49-außenspiegel-rechts :50-schlüssel/-karten :51-zierleiste :52-scheibenwischer-hinten :53-radkappen :54-auspuffanlage :55-bodengruppe :56-fahrersitz :57-sonstige-sitze :58-katalysator :59-achse :60-getriebe :61-reserverad :62-kompressor :63-pannenset :64-werkzeug :65-wagenheber :66-verbandskasten :67-warndreieck :68-laderaumabdeckung :69-fußmatten :70-handy :71-antenne :72-sommerräder :73-winterräder :74-aschenbecher :75-bordmappe :76-zubehörlieferschein :77-radio :78-cd/dvd-wechsler :79-navigationsgerät :80-zigarettenanzünder])


(def inside-damage-regions [{:label "Vordersitze"}
                            {:label "Rücksitze"}
                            {:label "Innenverkleidung"}
                            {:label "Teppichboden"}
                            {:label "Dachhimmel"}
                            {:label "Kofferraum/Ladefläche"}
                            {:label "Armaturentafel/Mittelkonsole"}])

(def outside-damage-regions [{:label "Spoiler"} {:label "Stoßstange vorne"} {:label "Frontgrill", :polygon [[30 -17] [26 -3] [75 -1] [72 -17]]} {:label "Scheinwerfer links"} {:label "Scheinwerfer rechts"} {:label "Motorhaube", :polygon [[21 -2] [27 -3] [74 -2] [79 1] [74 21] [69 24] [59 20] [38 20] [31 23] [25 21]]} {:label "Schiebedach"} {:label "Seitenteil vorne links", :polygon [[3 -4] [4 7] [15 8] [17 17] [11 23] [6 23] [1 24] [2 28] [23 27] [29 30] [25 18] [21 -6] [11 -8]]} {:label "Seitenteil vorne rechts", :polygon [[96 8] [95 -4] [88 -7] [79 -3] [75 20] [75 26] [73 32] [78 27] [99 28] [99 23] [88 22] [83 15] [86 9] [93 7]]} {:label "Türe vorne links", :polygon [[2 28] [1 52] [25 52] [24 26]]} {:label "Türe vorne rechts", :polygon [[77 26] [99 27] [99 52] [75 52]]} {:label "Türe hinten links", :polygon [[1 51] [25 51] [25 75] [16 74] [13 71] [6 69] [1 68]]} {:label "Türe hinten rechts", :polygon [[75 52] [99 51] [100 70] [91 69] [88 69] [83 75] [75 74]]} {:label "Schweller links"} {:label "Schweller rechts"} {:label "Seitenteil hinten links", :polygon [[16 74] [25 74] [25 83] [30 84] [30 99] [21 104] [1 104] [1 86] [8 86] [14 84] [17 78]]} {:label "Seitenteil hinten rechts", :polygon [[75 73] [83 75] [84 81] [89 86] [99 85] [100 102] [89 105] [78 104] [71 99] [70 84] [76 83]]} {:label "Tankklappe"} {:label "Dach", :polygon [[33 39] [34 75] [51 78] [66 75] [66 40]]} {:label "Heckdeckel", :polygon [[30 83] [30 99] [71 99] [71 84] [51 87]]} {:label "Heckblech", :polygon [[22 103] [31 98] [71 98] [80 103] [78 118] [23 117]]} {:label "Stoßstange hinten"} {:label "Heckschürze"} {:label "Heckleuchte links"} {:label "Heckleuchte rechts"} {:label "Scheibenwischer links"} {:label "Scheibenwischer rechts"} {:label "Ausstellfenster links vorne"} {:label "Ausstellfenster rechts vorne"} {:label "Frontscheibe", :polygon [[28 21] [23 20] [33 40] [67 40] [76 24] [74 20] [69 23] [58 19] [39 18] [35 21]]} {:label "Seitenscheibe vorne links", :polygon [[23 27] [24 52] [35 53] [34 40] [28 28]]} {:label "Seitenscheibe vorne rechts", :polygon [[78 28] [76 52] [65 53] [66 40] [74 29]]} {:label "Seitenscheibe hinten links", :polygon [[24 51] [35 53] [34 75] [24 83]]} {:label "Seitenscheibe hinten rechts", :polygon [[65 53] [76 51] [76 84] [66 74]]} {:label "Dreiecksfenster hinten links"} {:label "Dreiecksfenster hinten rechts"} {:label "Heckscheibe", :polygon [[33 74] [51 77] [66 74] [76 83] [51 87] [24 83]]} {:label "Reifen vorne links"} {:label "Felge vorne links", :polygon [[3 5] [8 5] [13 6] [17 11] [18 17] [11 24] [1 24] [1 7]]} {:label "Reifen vorne rechts"} {:label "Felge vorne rechts", :polygon [[99 7] [92 6] [85 8] [82 13] [84 20] [90 23] [99 23]]} {:label "Reifen hinten links"} {:label "Felge hinten links", :polygon [[1 68] [8 68] [16 72] [18 78] [14 85] [7 87] [0 87]]} {:label "Reifen hinten rechts"} {:label "Felge hinten rechts", :polygon [[100 69] [94 68] [87 69] [82 75] [84 82] [88 86] [99 86]]} {:label "Außenspiegel links"} {:label "Batterie"} {:label "Innenspiegel"} {:label "Außenspiegel rechts"} {:label "Karten"} {:label "Zierleiste"} {:label "Scheibenwischer hinten"} {:label "Radkappen"} {:label "Auspuffanlage"} {:label "Bodengruppe"} {:label "Fahrersitz"} {:label "Sonstige sitze"} {:label "Katalysator"} {:label "Achse"} {:label "Getriebe"} {:label "Reserverad"} {:label "Kompressor"} {:label "Pannenset"} {:label "Werkzeug"} {:label "Wagenheber"} {:label "Verbandskasten"} {:label "Warndreieck"} {:label "Laderaumabdeckung"} {:label "Fußmatten"} {:label "Handy"} {:label "Antenne"} {:label "Sommerräder"} {:label "Winterräder"} {:label "Aschenbecher"} {:label "Bordmappe"} {:label "Zubehörlieferschein"} {:label "Radio"} {:label "Wechsler"} {:label "Navigationsgerät"} {:label "Zigarettenanzünder"}])


#_(def damage-regions (str (mapv (fn [k]
                                   (cond-> {:label (str/capitalize (str/join " " (rest (str/split (name k) #"-"))))}
                                     (get polygons k)
                                     (assoc :polygon (get polygons k)))) regions)))

(defn damages-drawing [damages]
  [:div.rounded-lg.mx-3.bg-white.mt-3.relative
   [:svg.absolute.w-full.h-full {:viewBox "0 0 1930 2625"
                                 :xmlns "http://www.w3.org/2000/svg"}
    (map-indexed (fn [idx {pos :damage/position}]
                   (when pos
                     [damage-badge* {:num idx :pos pos :key idx}]))
                 damages)
    (map (fn [[k ps]]
           [invisible-polygon {:ps ps
                               :key k}]) polygons)]
   [:img {:src "/images/car-explosion.svg"}]])

(defn damages-selector [path]
  [:div.rounded-lg.mx-3.bg-white.mt-3.relative
   [:svg.absolute.w-full.h-full {:viewBox "0 0 100 100"
                                 :xmlns "http://www.w3.org/2000/svg"
                                 :on-pointer-up (fn [e]
                                                  (let [target (.-target e)
                                                        region-key (when (= (.-tagName target) "polygon")
                                                                     (.-key (.-dataset target)))]
                                                    (when (= (.-tagName target) "polygon")
                                                      ;; flash the opacity of the target region so user can see region they clicked
                                                      (set! (.-opacity (.-style target)) "0.25")
                                                      (js/setTimeout (fn []
                                                                       (set! (.-opacity (.-style target)) "0")) 150))

                                                    (add-new-damage! path {:key (js/Math.random)
                                                                           :region region-key
                                                                           :pos (eventToSvgCoordinates e)
                                                                           :comment ""})))}
    (map-indexed (fn [idx {pos :pos}]
                   (when pos
                     [damage-badge* {:num idx :pos pos :key idx}]))
                 @(deframe/!route-get-in path []))
    (map (fn [[k ps]]
           [invisible-polygon {:ps ps
                               :key k}]) polygons)]
   [:img {:src "/images/car-explosion.svg"}]])

(defn mini-damage-viewer [idx pos]
  [:div.relative
   [:svg.absolute.w-full.h-full {:viewBox "0 0 100 100"
                                 :xmlns "http://www.w3.org/2000/svg"}
    [:circle {:fill "#eb6c23"
              :stroke "white"
              :stroke-width "3"
              :cx (first pos)
              :cy (second pos)
              :r 10}]]
   [:img {:src "/images/car-explosion.svg"}]])

(defn damages-polygon-editor []
  (let [!exploded-ref (hooks/use-ref nil)
        !text-ref (hooks/use-ref nil)
        i @(deframe/!route-get ::i 0)]
    (hooks/use-effect (fn []
                        (deframe/route-assoc! ::p polygons)))
    [:div {:style {:max-width "450px"}}
     [:h1 "polygon editor"]
     [:span "current polygon name:"]
     [:textarea.border.shadow-inner.w-full.rounded-md.mb-0.block.text-sm.px-2.py-1.h-8
      {:placeholder (tr "Please describe the damage in as much detail as possible.")
       :ref !text-ref
       :default-value @(deframe/!route-get ::i)
       :on-change (fn [e]
                    (deframe/route-assoc! ::i (keyword (.-value (.-target e)))))}]
     [:button {:on-pointer-up #(deframe/route-dissoc-in! [::p i])} "❌ clear current polygon"]
     [:div.rounded-lg.mx-3.bg-white.mt-3.relative
      [:svg.absolute.w-full.h-full {:viewBox "0 0 100 100"
                                    :xmlns "http://www.w3.org/2000/svg"
                                    :ref !exploded-ref
                                    :on-pointer-up (fn [e]
                                                     (let [target (.-target e)]
                                                       (when (= (.-tagName target) "polygon")
                                                         (js/console.log (.-key (.-dataset target)) regions (.indexOf regions (keyword (.-key (.-dataset target)))))))
                                                     (.preventDefault e)
                                                     (.focus @!text-ref)
                                                     (deframe/route-assoc-in! [::p i] (conj @(deframe/!route-get-in [::p i] [])
                                                                                            (eventToSvgCoordinates e))))}
       (map (fn [[k ps]]
              [polygon {:ps ps
                        :key k}]) @(deframe/!route-get-in [::p] []))]
      [:img {:src "/images/car-explosion.svg"}]]
     [:span "ctrl+a to select all and replace the polygons data structure in code with the following"]
     [:textarea.block.text-sm.px-2.py-1.w-full.pre
      {:readOnly true
       :value (str (into {} (map (fn [[k ps]]
                                   [k (mapv (fn [[x y]] [(js/Number (.toFixed (* 100 x) 2)) (js/Number (.toFixed (* 100 y) 2))]) ps)])  @(deframe/!route-get-in [::p] []))))}]]))

(def damage-types [{:label "Delle" :details {:label "Durchmesser" :value nil :unit "cm"}}
                   {:label "Beule"}
                   {:label "Kratzer" :details {:label "Länge" :value nil :unit "cm"}}
                   {:label "Bruch"}
                   {:label "Lackschaden" :details {:label "art" :value nil}}
                   {:label "verschmutzt"}
                   {:label "Reifenschäden"}
                   {:label "defekt" :details {:value nil}}
                   {:label "Fehlteil" :details {:value nil}}])

;; source: https://stackoverflow.com/a/4831131
;; returns indexes of elements of the given coll that match the given predicate.
(defn positions
  [pred coll]
  (keep-indexed (fn [idx x]
                  (when (pred x)
                    idx))
                coll))

(defn select* [{:keys [opts selection on-select]}]
  [:select.border.w-full.rounded-md.px-2.py-1.text-sm
   {:on-change (fn [e]
                 (on-select (js/parseInt (.-value (.-target e)))))
    :value (or (first (positions #(= selection (:label %)) opts)) -1)}
   (concat [[:option {:value -1 :key "select"} "select a damage"]]
           (map-indexed (fn [idx {:keys [label]}]
                          [:option {:value idx
                                    :key label}
                           label])
                        opts))])

(defn damage [{:keys [index
                      pos
                      fahrzeug
                      region
                      type
                      type-detail
                      comment
                      damage-regions
                      on-remove
                      on-change-comment
                      on-select-region
                      on-select-type
                      on-change-type-detail]}]
  (r/with-let [!notice (r/atom nil)
               !state (r/atom {:taking-photo? false})]
    (let [{:keys [damaged? taking-photo?] :as state} @!state]
      [:div.flex.rounded-lg.mx-3.bg-white.mt-3.p-3.gap-3
       [:div [damage-badge {:num index}]
        (when pos [mini-damage-viewer index pos])]
       [:div.grow
        [:label.text-sm.text-gray-500.block.mb-1 (tr "damage region")
         [select* {:opts damage-regions :selection region :on-select on-select-region}]]
        [:label.text-sm.text-gray-500.block.mb-2 (tr "damage type")
         [select* {:opts damage-types :selection type :on-select on-select-type}]]
        (when-let [details (some #(when (= (:label %) type) (:details %)) damage-types)]
          [:label.text-sm.text-gray-500.flex.items-center.gap-2.mb-2 (:label details)
           [:input.border.shadow-inner.w-full.rounded-md.mb-0.block.text-sm.px-2.py-1
            {:type "text"
             :value type-detail
             :placeholder (str/join " - " [type (:label details)])
             :on-change (fn [e] (on-change-type-detail (.-value (.-target e))))}]
           (:unit details)])
        [:textarea.border.shadow-inner.w-full.rounded-md.mb-0.block.text-sm.px-3.py-2
         {:placeholder (tr "Please describe the damage in as much detail as possible.")
          :default-value comment
          :on-change (fn [e] (on-change-comment (.-value (.-target e))))}]
        (if taking-photo?
          [workflow/photo-mode {:entity fahrzeug
                                :!state !state
                                ;; to ignore:
                                :notice (r/atom nil)}]
          [damage-photos {:entity fahrzeug
                          :notice !notice
                          :!state !state}])
        [:button.text-red-500.underline.float-right.pt-2 {:on-pointer-up on-remove}
         (tr "remove damage")
         [ui/icon {:width 24 :height 24 :style {:display "inline"}} :trash]]]])))

(defn damages-list [{:keys [path fahrzeug] :damage-report/keys [location]}]
  (let [damage-regions (case location
                         :damage-report.location/inside inside-damage-regions
                         outside-damage-regions)]
    [:<>
     [:> framer-motion/AnimatePresence
      (map-indexed (fn [idx {:keys [comment pos region key type type-detail]}]
                     [:> (.-div framer-motion/motion)
                      {:key key
                       :initial {:opacity 0 :scale 0}
                       :animate {:opacity 1 :scale 1}
                       :exit {:opacity 0 :scale 0}
                       :transition {:type "tween"}}
                      [damage {:index idx
                               :pos pos
                               :fahrzeug fahrzeug
                               :comment comment
                               :region region
                               :type type
                               :type-detail type-detail
                               :damage-regions damage-regions
                               :on-change-comment (fn [comment] (modify-damage! path idx {:comment comment}))
                               :on-select-region (fn [selected-idx]
                                                   (let [selected-region (nth damage-regions selected-idx nil)]
                                                     (if (:polygon selected-region)
                                                       (modify-damage! path idx {:pos (polygon-centroid (:polygon selected-region))
                                                                                 :region (:label selected-region)})
                                                       (modify-damage! path idx {:region (:label selected-region)}))))
                               :on-select-type (fn [selected-idx]
                                                 (let [selected-type (nth damage-types selected-idx nil)]
                                                   (modify-damage! path idx {:type (:label selected-type)
                                                                             :type-detail nil})))
                               :on-change-type-detail #(modify-damage! path idx {:type-detail %})
                               :on-remove (fn [] (remove-damage! path idx))}]])
                   @(deframe/!route-get-in path []))]
     [:div.p-3
      [mobile/button-outline {:on-pointer-up (fn [e]
                                               (add-new-damage! path {:key (js/Math.random)}))}
       (tr "Add another damage")]]]))

(defn report-content [{:as args :keys [path] :damage-report/keys [location]}]
  [:<>
   (case location
     :damage-report.location/inside nil
     [damages-selector path])
   #_[damages-polygon-editor]
   [damages-list args]])

(defn report
  ([fahrzeug] (report [::damages] fahrzeug))
  ([path fahrzeug]
   [mobile/scroll-content
    [transport-ops-vehicle-header fahrzeug]
    [report-content path fahrzeug]
    [:div.p-3
     [mobile/button-full (tr "Submit")]]]))

(defn render-report [fahrzeug _match]
  [report fahrzeug])

(comment
  (keys fzg)
  (:vehicle/equipment fzg))
